<template>
  <q-card-section
    class="onboarding-terms-panel column col items-center gap-lg q-pt-xl q-px-xl q-pb-md"
  >
    <div class="onboarding-icon-badge row items-center justify-center">
      <lucide-icon
        :name="copy.icon"
        size="40px"
        :stroke-width="2"
        aria-hidden="true"
      />
    </div>

    <div class="column items-center gap-md text-center">
      <h1 class="text-28 text-font-semibold">{{ copy.headline }}</h1>
      <div
        v-if="copy.subline?.length"
        class="onboarding-subline column items-center gap-xs text-16 text-primary"
      >
        <p
          v-for="(line, index) in copy.subline"
          :key="index"
          class="q-ma-none"
        >
          {{ line }}
        </p>
      </div>
    </div>

    <q-form
      class="column col-grow justify-between gap-lg full-width"
      @submit.prevent="onSubmit"
    >
      <terms-content
        :version="LATEST_TERMS_VERSION"
        class="col-grow full-width"
      />

      <div class="column items-center gap-xs">
        <q-btn
          flat
          no-caps
          color="secondary"
          class="onboarding-action-btn sm-radius text-14"
          @click="onUseDifferentAccount"
        >
          {{ ONBOARDING_COPY.secondaryActionLabel }}
        </q-btn>
        <p
          class="onboarding-subline text-12 text-secondary-light text-font-regular text-center line-height-md"
        >
          {{ copy.disclaimer }}
        </p>
        <q-btn
          type="submit"
          no-caps
          unelevated
          color="brand"
          class="onboarding-action-btn sm-radius text-14 text-font-semibold"
          :loading="isSubmitting"
          :disable="isSubmitting"
        >
          {{ copy.ctaLabel }}
          <lucide-icon
            :name="copy.ctaIcon"
            size="18px"
            color="white"
            class="q-ml-xs"
            aria-hidden="true"
          />
        </q-btn>
      </div>
    </q-form>
  </q-card-section>
</template>

<script setup>
  import { ref } from 'vue'

  import { useAccountActionsStore } from 'src/stores/account/account-actions'
  import { useOnboardingStore } from 'src/stores/account/onboarding'
  import { useAuthStore } from 'src/stores/auth'

  import { ONBOARDING_COPY, ONBOARDING_FLOW_TYPE } from 'src/services/consts/app.const'
  import { LATEST_TERMS_VERSION } from 'src/assets/content/terms/index'

  import TermsContent from 'src/components/terms/TermsContent.vue'

  const accountActionsStore = useAccountActionsStore()
  const onboardingStore = useOnboardingStore()
  const authStore = useAuthStore()

  const copy = ONBOARDING_COPY.termsWelcome
  const isSubmitting = ref(false)

  // The account already exists server-side, so this only abandons setup — the
  // next sign-in lands back on the welcome step.
  function onUseDifferentAccount() {
    authStore.logout()
  }

  async function onSubmit() {
    isSubmitting.value = true

    try {
      const { accountName, companyName, country, firstName, lastName } = onboardingStore.pendingForm

      const completed = await accountActionsStore.completeOnboarding({ firstName, lastName })
      if (!completed) return

      if (onboardingStore.flowType === ONBOARDING_FLOW_TYPE.FOUNDER) {
        const setup = await accountActionsStore.setupAccount({ accountName, companyName, country })
        if (!setup) return
      }

      onboardingStore.resetState()
    } finally {
      isSubmitting.value = false
    }
  }
</script>

<style lang="scss">
  .onboarding-terms-panel .terms-content-body {
    max-height: calc(100vh - 34rem);
  }
</style>
